/**
 * Setup Password — public landing for the one-time setup link emailed
 * to new users immediately after admin invites them. The link contains
 * a 64-char URL-safe token (?token=...) that's hashed + stored backend-side.
 *
 * Flow:
 *   1. On mount → GET /auth/setup-password/verify?token=... to greet
 *      the user by name + confirm the link is still valid.
 *   2. User types new password + confirm → POST /auth/setup-password.
 *   3. On success → redirect to /login with success toast.
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { motion } from "framer-motion";
import { CheckCircle, WarningCircle, ShieldCheck, Eye, EyeSlash, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SetupPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying / valid / invalid / done
  const [meta, setMeta] = useState(null);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("invalid"); return;
    }
    api.get("/auth/setup-password/verify", { params: { token } })
      .then(({ data }) => {
        if (data.valid) { setStatus("valid"); setMeta(data); }
        else { setStatus("invalid"); setMeta(data); }
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (!/[A-Z]/.test(pw) || !/[a-z]/.test(pw) || !/\d/.test(pw))
      return setErr("Use a mix of upper, lower & at least one number.");
    if (pw !== confirm) return setErr("Both passwords must match.");
    setSubmitting(true);
    try {
      await api.post("/auth/setup-password", {
        token, new_password: pw, confirm_password: confirm,
      });
      setStatus("done");
      toast.success("Password set! Sign in below.");
      setTimeout(() => nav("/login"), 1800);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-paper via-paper-alt to-mustard-soft/30 flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white rounded-3xl border border-border p-8 shadow-soft"
        data-testid="setup-password-page">
        <div className="text-center mb-6">
          <Logo size={48} tagline={false} />
        </div>

        {status === "verifying" && (
          <div className="text-center py-10" data-testid="sp-verifying">
            <div className="w-12 h-12 mx-auto rounded-full border-2 border-terracotta border-t-transparent animate-spin" />
            <p className="text-sm text-ink-soft mt-4">Verifying your setup link…</p>
          </div>
        )}

        {status === "invalid" && (
          <div className="text-center py-6" data-testid="sp-invalid">
            <WarningCircle weight="duotone" size={48} className="mx-auto text-rose-500" />
            <h1 className="font-heading text-xl font-bold mt-3">Link no longer valid</h1>
            <p className="text-sm text-ink-soft mt-2">
              {meta?.reason === "consumed"
                ? "You've already set your password with this link. Try signing in instead."
                : meta?.reason === "expired"
                ? "This link has expired. Ask your admin to send you a new one."
                : "This setup link is invalid or has already been used."}
            </p>
            <Link to="/login" data-testid="sp-go-login"
              className="inline-block mt-5 px-6 py-2.5 rounded-full bg-terracotta text-white text-sm font-semibold">
              Go to sign in
            </Link>
          </div>
        )}

        {status === "valid" && (
          <form onSubmit={submit} data-testid="sp-form">
            <div className="flex items-center gap-2 mb-1">
              <Sparkle weight="duotone" className="text-mustard-deep" size={20} />
              <span className="text-[11px] uppercase tracking-wider font-semibold text-mustard-deep">Welcome to MangoCubs</span>
            </div>
            <h1 className="font-heading text-2xl font-bold">Hi {meta?.name?.split(" ")[0] || "there"} 👋</h1>
            <p className="text-sm text-ink-soft mt-1 mb-5">
              Set a password for <span className="font-mono text-ink">{meta?.email_masked}</span>. You can change it anytime from your profile.
            </p>

            <label className="block mb-3">
              <div className="text-xs font-medium text-ink-soft mb-1">New password</div>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="input pr-10" required minLength={8}
                  placeholder="At least 8 characters, mixed case + a digit"
                  data-testid="sp-pw"
                  autoFocus
                />
                <button type="button" onClick={() => setShow(!show)}
                  className="absolute inset-y-0 right-2 px-2 text-ink-soft" tabIndex={-1}
                  data-testid="sp-toggle">
                  {show ? <EyeSlash weight="duotone" /> : <Eye weight="duotone" />}
                </button>
              </div>
            </label>
            <label className="block mb-3">
              <div className="text-xs font-medium text-ink-soft mb-1">Confirm password</div>
              <input
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="input" required minLength={8}
                data-testid="sp-confirm"
              />
            </label>

            <PasswordStrength pw={pw} />

            {err && (
              <div className="mt-3 text-sm text-rose-500" data-testid="sp-error">{err}</div>
            )}

            <button type="submit" disabled={submitting}
              data-testid="sp-submit"
              className="mt-5 w-full h-11 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover disabled:opacity-60 flex items-center justify-center gap-2">
              {submitting ? "Setting password…" : (<><ShieldCheck weight="bold" /> Set my password</>)}
            </button>
            <p className="text-[11px] text-ink-soft text-center mt-3">
              By continuing you agree to MangoCubs' terms · privacy
            </p>
          </form>
        )}

        {status === "done" && (
          <div className="text-center py-6" data-testid="sp-done">
            <CheckCircle weight="duotone" size={56} className="mx-auto text-sage-deep" />
            <h1 className="font-heading text-xl font-bold mt-3">All set!</h1>
            <p className="text-sm text-ink-soft mt-2">Redirecting you to sign in…</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function PasswordStrength({ pw }) {
  if (!pw) return null;
  const checks = [
    { ok: pw.length >= 8, label: "8+ characters" },
    { ok: /[A-Z]/.test(pw), label: "Upper-case letter" },
    { ok: /[a-z]/.test(pw), label: "Lower-case letter" },
    { ok: /\d/.test(pw), label: "A number" },
    { ok: /[^A-Za-z0-9]/.test(pw), label: "Symbol (optional)" },
  ];
  const score = checks.slice(0, 4).filter((c) => c.ok).length;
  return (
    <div className="mt-2" data-testid="sp-strength">
      <div className="flex gap-1 h-1 mb-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`flex-1 rounded ${
            score >= i ? ["bg-rose-400", "bg-amber-400", "bg-amber-400", "bg-sage-deep"][i - 1] : "bg-paper-alt"
          }`} />
        ))}
      </div>
      <ul className="text-[11px] text-ink-soft space-y-0.5">
        {checks.map((c) => (
          <li key={c.label} className={c.ok ? "text-sage-deep" : ""}>
            {c.ok ? "✓" : "○"} {c.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
